import { Box, Paper, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { Table } from 'Common/MUITableV2'
import {
  formatInt,
  formatNumber,
  formatPct,
  useUtilisationData,
} from './Utilisation.data'
import type { CrewUtilisationRow } from './Utilisation.types'

type Props = {
  schedulerId: string
  iteration: string
}

const isCrewResource = (name: string) => /crew/i.test(name)

const COLS: GridColDef<CrewUtilisationRow>[] = [
  { field: 'resource_name', headerName: 'Crew', flex: 1, minWidth: 140 },
  {
    field: 'hours_used',
    headerName: 'Hours Used',
    type: 'number',
    flex: 1,
    minWidth: 110,
    valueFormatter: (value: number) => formatNumber(value, 1),
  },
  {
    field: 'capacity',
    headerName: 'Capacity',
    type: 'number',
    flex: 1,
    minWidth: 100,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'horizon_hours',
    headerName: 'Horizon',
    type: 'number',
    flex: 1,
    minWidth: 90,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'utilisation_pct',
    headerName: 'Utilisation',
    type: 'number',
    flex: 1,
    minWidth: 110,
    valueFormatter: (value: number) => formatPct(value),
  },
]

const TableSection = ({
  title,
  rows,
  loading = false,
}: {
  title: string
  rows: CrewUtilisationRow[]
  /** Draws the grid's skeleton overlay in place of rows. */
  loading?: boolean
}) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Typography
      variant="subtitle2"
      sx={{
        textAlign: 'center',
        fontWeight: 700,
        color: 'primary.main',
        mb: 1,
      }}
    >
      {title}
    </Typography>
    <Table
      loading={loading}
      // The skeleton draws ceil(viewportHeight / rowHeight) rows, and this
      // grid is `autoHeight` — with no rows that viewport is zero and the
      // overlay comes out empty. The floor only applies while loading, so
      // the loaded table still sizes to its content.
      sx={loading ? { minHeight: 220 } : undefined}
      rows={rows}
      columns={COLS as GridColDef[]}
      getRowId={(r: any) => `${r.side}-${r.resource_name}`}
      density="compact"
      hideFooter
      disableRowSelectionOnClick
      autoHeight
      getRowClassName={(p) =>
        (p.indexRelativeToCurrentPage ?? 0) % 2 === 0 ? 'row--even' : 'row--odd'
      }
    />
  </Paper>
)

const Kpi = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      px: 2,
      py: 1,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      flex: 1,
      minWidth: 160,
    }}
  >
    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>{value}</Typography>
  </Box>
)

export const CrewUtilisationView = ({ schedulerId, iteration }: Props) => {
  const { data, isLoading, error } = useUtilisationData(schedulerId, iteration)

  // Kitchen crews only. The line-side "crews" (East_Crew / West_Crew) are
  // packing-line constraints rather than crews — client feedback 2026-10-06 —
  // and are shown with the other resources in the usage heat map instead.
  const kitchenCrews = useMemo(
    () =>
      (data?.crew_utilisation ?? []).filter(
        (r) => isCrewResource(r.resource_name) && r.side === 'kitchen'
      ),
    [data]
  )

  // The real layout with a skeleton grid, rather than a spinner
  // in the middle of an empty page — the sections do not move when the data
  // lands. The KPI strip is left out on purpose: it is four numbers, and
  // showing zeros or dashes for them reads as a result rather than a wait.
  if (isLoading) {
    return <TableSection title="Kitchen Crew Utilisation" rows={[]} loading />
  }
  if (error || !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Could not load utilisation data
          {error instanceof Error ? `: ${error.message}` : ''}.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Kpi
          label="Number of HALBs"
          value={formatInt(data.summary.num_halbs)}
        />
        <Kpi
          label="Total Plan Tonnage"
          value={formatNumber(data.summary.total_tonnage_kg / 1000, 0)}
        />
        <Kpi label="Number of FGs" value={formatInt(data.summary.num_fgs)} />
        <Kpi
          label="Total Plan Cases"
          value={formatInt(data.summary.total_cases)}
        />
      </Box>

      <TableSection title="Kitchen Crew Utilisation" rows={kitchenCrews} />
    </Box>
  )
}
